import { Link } from "react-router-dom";
import { useScrollRevealChildren } from "../../../hooks/useScrollReveal";

export interface WhyReason {
  icon: string;
  title: string;
  description: string;
}

export interface WhyChooseContent {
  badge?: string;
  headline: string;
  headlineAccent?: string;
  subtext: string;
  reasons: WhyReason[];
  ctaLabel: string;
  ctaPath: string;
}

interface WhyChooseUsProps {
  content: WhyChooseContent;
}

export default function WhyChooseUs({ content }: WhyChooseUsProps) {
  const { badge, headline, headlineAccent, subtext, reasons, ctaLabel, ctaPath } = content;
  const headerRef = useScrollRevealChildren();
  const listRef = useScrollRevealChildren();

  return (
    <section className="bg-white py-28 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-6 xl:px-10">

        {/* Header */}
        <div ref={headerRef} className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-10">
          <div className="max-w-xl">
            {badge && (
              <div className="reveal flex items-center gap-4 mb-6">
                <div className="h-px w-12 bg-[#C9A961]" />
                <span
                  className="text-[#C9A961] text-[10px] tracking-[0.3em] uppercase font-medium"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {badge}
                </span>
              </div>
            )}
            <h2
              className="reveal delay-100 text-4xl md:text-5xl xl:text-[58px] text-[#1C1C1C] leading-tight mb-5"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, letterSpacing: "-0.01em" }}
            >
              {headline}
              {headlineAccent && (
                <em className="block text-[#C9A961]" style={{ fontStyle: "italic" }}>
                  {headlineAccent}
                </em>
              )}
            </h2>
            <p
              className="reveal delay-200 text-[#777] text-[15px] leading-relaxed"
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
            >
              {subtext}
            </p>
          </div>
          <Link
            to={ctaPath}
            className="reveal delay-300 bg-[#C9A961] text-white text-[10px] tracking-[0.22em] uppercase font-semibold px-10 py-4 hover:bg-[#b8964f] transition-all duration-300 cursor-pointer whitespace-nowrap self-start lg:self-end"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {ctaLabel}
          </Link>
        </div>

        {/* Numbered horizontal list */}
        <div ref={listRef} className="border-t border-[#F0EBE2]">
          {reasons.map((reason, idx) => (
            <div
              key={reason.title}
              className={`reveal delay-${idx * 80} group grid grid-cols-12 gap-6 items-center py-8 border-b border-[#F0EBE2] hover:bg-[#FDFAF6] transition-all duration-400 px-4 -mx-4 cursor-default`}
            >
              {/* Large decorative number */}
              <div className="col-span-1 hidden lg:block text-right pr-2">
                <span
                  className="text-[52px] leading-none font-bold text-[#1C1C1C]/[0.055] group-hover:text-[#C9A961]/15 transition-colors duration-500"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
              </div>

              {/* Icon */}
              <div className="col-span-1 hidden sm:flex justify-center">
                <div className="w-12 h-12 flex items-center justify-center border border-[#E8E2D9] group-hover:border-[#C9A961]/60 group-hover:bg-[#C9A961]/5 transition-all duration-400">
                  <i className={`${reason.icon} text-[#C9A961] text-xl`} />
                </div>
              </div>

              {/* Title */}
              <div className="col-span-12 sm:col-span-4 lg:col-span-3">
                <h3
                  className="text-[#1C1C1C] text-xl font-bold group-hover:text-[#C9A961] transition-colors duration-300 leading-snug"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {reason.title}
                </h3>
              </div>

              {/* Vertical divider */}
              <div className="hidden md:flex col-span-1 justify-center">
                <div className="w-px h-8 bg-[#E8E2D9] group-hover:bg-[#C9A961]/35 transition-colors duration-400" />
              </div>

              {/* Description */}
              <div className="col-span-12 sm:col-span-12 md:col-span-5 lg:col-span-5">
                <p
                  className="text-[#666] text-sm leading-[1.85]"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                >
                  {reason.description}
                </p>
              </div>

              {/* Arrow */}
              <div className="hidden lg:flex col-span-1 justify-end">
                <div className="w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-400">
                  <i className="ri-arrow-right-line text-[#C9A961] text-base" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
